import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ClockCounterClockwise,
  ArrowUpRight,
  Trash,
  CheckSquare,
  Square,
} from "@phosphor-icons/react";
import { getToolByKey } from "../lib/tools";
import { renderMarkdown } from "../lib/markdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function historyErrorMessage(err, fallback = "History action failed.") {
  return (
    err?.response?.data?.detail ||
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    fallback
  );
}

function safeHistoryInputs(value) {
  if (!value) return {};

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function cleanHistoryLabel(value, fallback = "Generation") {
  const text = String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!text) return fallback;

  return text.replace(/\b\w/g, (char) => char.toUpperCase());
}

function historyTypeLabel(item) {
  const inputs = safeHistoryInputs(item?.inputs);
  const raw = String(
    inputs.history_type ||
      inputs.type ||
      inputs.content_type ||
      item?.history_type ||
      ""
  )
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");

  const labels = {
    checklist: "Checklist",
    gbp_posts: "GBP posts",
    "gbp-posts": "GBP posts",
    linkedin_post: "LinkedIn post",
    wordpress_post: "WordPress post",
    wordpress_landing_page: "WordPress landing page",
    blog_post: "Blog post",
  };

  return labels[raw] || cleanHistoryLabel(raw, "");
}

function historySourceLabel(item) {
  const inputs = safeHistoryInputs(item?.inputs);
  const source = String(inputs.source || item?.source || "").trim().toLowerCase();

  if (source === "workflow_center") return "Workflow Center";
  if (source) return cleanHistoryLabel(source);

  return "";
}

function isGenericProjectLabel(value) {
  const text = String(value || "").trim().toLowerCase();
  return text === "" || text === "project" || text === "projects" || text === "unknown project";
}

function historyProjectInfo(item) {
  const inputs = safeHistoryInputs(item?.inputs);
  const project = safeHistoryInputs(inputs.project || inputs._project || item?.project);

  const rawName = String(
    inputs.project_name ||
      inputs.projectName ||
      inputs._project?.name ||
      project.name ||
      project.project_name ||
      item?.project_name ||
      ""
  ).trim();

  const rawId = String(
    inputs.project_id ||
      inputs.projectId ||
      inputs._project?.project_id ||
      project.project_id ||
      project.id ||
      item?.project_id ||
      ""
  ).trim();

  const rawStatus = String(
    inputs.project_status ||
      inputs.projectStatus ||
      inputs._project?.status ||
      project.status ||
      item?.project_status ||
      ""
  ).trim().toLowerCase();

  const name = isGenericProjectLabel(rawName) ? "" : rawName;

  if (!name && !rawId && !rawStatus) {
    return null;
  }

  return {
    id: rawId,
    name: name || rawId || "",
    status: rawStatus || "active",
  };
}

function historyProjectLabel(item) {
  const project = historyProjectInfo(item);

  if (!project?.name || isGenericProjectLabel(project.name)) {
    return "";
  }

  return ["archived", "deleted"].includes(project.status)
    ? `${project.name} archived`
    : project.name;
}

function historyDisplayTitle(item) {
  const inputs = safeHistoryInputs(item?.inputs);
  const tool = getToolByKey(item?.tool);
  const fallback = tool?.name || cleanHistoryLabel(item?.tool, "Generation");

  const candidates = [
    inputs.display_title,
    inputs.history_title,
    inputs.title,
    inputs.workflow_title,
    inputs.name,
    item?.title,
  ];

  const found = candidates.find((value) => String(value || "").trim() !== "");
  return found ? String(found).trim() : fallback;
}

function historyMetaBadges(item) {
  const tool = getToolByKey(item?.tool);
  const projectLabel = historyProjectLabel(item);
  const sourceLabel = historySourceLabel(item);
  const typeLabel = historyTypeLabel(item);
  const toolLabel = tool?.name || cleanHistoryLabel(item?.tool, "");

  return [projectLabel, sourceLabel, typeLabel, toolLabel]
    .filter(Boolean)
    .filter((value, index, array) => array.indexOf(value) === index);
}

function HistoryConfirmModal({ open, title, subtitle, message, confirmLabel, danger = false, loading, onClose, onConfirm }) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center px-4"
      onClick={loading ? undefined : onClose}
    >
      <div
        className="w-full max-w-lg bg-white border border-[#E5E7EB] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-[#E5E7EB] px-5 py-4">
          <p className={`font-mono text-xs uppercase tracking-[0.2em] ${danger ? "text-[#DC2626]" : "text-[#737373]"}`}>
            {subtitle || "Confirm action"}
          </p>
          <h3 className="font-display text-2xl font-black mt-1">{title}</h3>
        </div>

        <div className="px-5 py-5">
          {danger && (
            <div className="mb-4 border border-[#FECACA] bg-[#FEF2F2] px-4 py-3">
              <p className="text-sm font-bold text-[#B91C1C]">This action is permanent.</p>
              <p className="text-xs text-[#7F1D1D] mt-1">
                Deleted history cannot be restored later.
              </p>
            </div>
          )}
          <p className="text-sm text-[#525252] leading-relaxed whitespace-pre-line">{message}</p>
        </div>

        <div className="border-t border-[#E5E7EB] px-5 py-4 flex justify-end gap-3 bg-white">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="border border-[#E5E7EB] px-4 py-2 text-sm font-semibold hover:bg-[#F5F5F5] disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`${danger ? "bg-[#DC2626] hover:bg-[#B91C1C]" : "bg-[#0A0A0A] hover:bg-black"} text-white px-4 py-2 text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5`}
          >
            <Trash size={14} weight="bold" />
            {loading ? "Working..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function History() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null);
  const [open, setOpen] = useState(null);
  const [selected, setSelected] = useState([]);
  const [confirmAction, setConfirmAction] = useState({
    open: false,
    type: null,
    id: null,
    title: "",
    subtitle: "",
    message: "",
    confirmLabel: "",
  });

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const allSelected = items.length > 0 && selected.length === items.length;

  const loadHistory = async () => {
    setLoading(true);

    try {
      const { data } = await axios.get(`${API}/history`, {
        params: {
          limit: 500,
          range: "all",
        },
      });

      setItems([...(data.items || [])].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)));
      setSelected([]);
      closeConfirmAction();
    } catch (err) {
      toast.error(historyErrorMessage(err, "Could not load history."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    setSelected(allSelected ? [] : items.map((item) => item.id));
  };

  const closeConfirmAction = () => {
    if (!working) {
      setConfirmAction({
        open: false,
        type: null,
        id: null,
        title: "",
        subtitle: "",
        message: "",
        confirmLabel: "",
      });
    }
  };

  const askDeleteHistoryItem = (id) => {
    if (!id) return;

    const item = items.find((entry) => entry.id === id);
    const label = item ? historyDisplayTitle(item) : "this generation";

    setConfirmAction({
      open: true,
      type: "item",
      id,
      title: "Delete history item?",
      subtitle: "History cleanup",
      message: `This will permanently delete ${label} from your Ordeneli history.

This cannot be undone.`,
      confirmLabel: "Delete item",
    });
  };

  const askDeleteSelected = () => {
    if (selected.length === 0) return;

    setConfirmAction({
      open: true,
      type: "selected",
      id: null,
      title: "Delete selected history?",
      subtitle: "History cleanup",
      message: `This will permanently delete ${selected.length} selected history item${selected.length === 1 ? "" : "s"}.

This cannot be undone.`,
      confirmLabel: `Delete selected (${selected.length})`,
    });
  };

  const askClearHistory = () => {
    if (items.length === 0) return;

    setConfirmAction({
      open: true,
      type: "clear",
      id: null,
      title: "Clear all history?",
      subtitle: "Permanent deletion",
      message: `This will permanently delete all ${items.length} history item${items.length === 1 ? "" : "s"} from your Ordeneli account.

You will not be able to recover them later.`,
      confirmLabel: "Clear history forever",
    });
  };

  const confirmHistoryAction = async () => {
    if (confirmAction.type === "item") {
      await deleteHistoryItem(confirmAction.id);
      return;
    }

    if (confirmAction.type === "selected") {
      await deleteSelected();
      return;
    }

    if (confirmAction.type === "clear") {
      await clearHistory();
    }
  };

  const deleteHistoryItem = async (id) => {
    if (!id) return;

    setWorking(`delete:${id}`);

    try {
      await axios.delete(`${API}/history/${encodeURIComponent(id)}`);
      toast.success("History item deleted.");
      setItems((prev) => prev.filter((item) => item.id !== id));
      setSelected((prev) => prev.filter((item) => item !== id));
      if (open === id) setOpen(null);
      closeConfirmAction();
    } catch (err) {
      toast.error(historyErrorMessage(err, "Could not delete this item. Make sure the backend DELETE /api/history/:id route is added."));
    } finally {
      setWorking(null);
    }
  };

  const deleteSelected = async () => {
    if (selected.length === 0) return;

    setWorking("delete-selected");

    try {
      await axios.delete(`${API}/history`, {
        data: {
          ids: selected,
          mode: "selected",
        },
      });

      toast.success("Selected history deleted.");
      setItems((prev) => prev.filter((item) => !selectedSet.has(item.id)));
      if (selectedSet.has(open)) setOpen(null);
      setSelected([]);
      closeConfirmAction();
    } catch (err) {
      toast.error(historyErrorMessage(err, "Could not delete selected items. Make sure the backend DELETE /api/history route supports selected IDs."));
    } finally {
      setWorking(null);
    }
  };

  const clearHistory = async () => {
    if (items.length === 0) return;

    setWorking("clear-history");

    try {
      await axios.delete(`${API}/history`, {
        data: {
          mode: "all",
          clear_all: true,
        },
      });

      toast.success("History cleared.");
      setItems([]);
      setSelected([]);
      setOpen(null);
      closeConfirmAction();
    } catch (err) {
      toast.error(historyErrorMessage(err, "Could not clear history. Make sure the backend DELETE /api/history route is added."));
    } finally {
      setWorking(null);
    }
  };

  return (
    <div>
      <section className="border-b border-[#E5E7EB]">
        <div className="container-wide py-10 md:py-14 flex items-end justify-between gap-6 md:gap-8 flex-wrap">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7] mb-3">History</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Every generation, logged.</h1>
            <p className="mt-3 text-[#525252] max-w-xl text-sm sm:text-base">Browse, review, copy and clean up everything you generated.</p>
          </div>
          <Link to="/dashboard" className="btn-secondary" data-testid="history-new-btn">
            New generation <ArrowUpRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      <section className="container-wide py-12">
        {loading && (
          <div className="grid gap-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-[#F5F5F5] animate-pulse" />
            ))}
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="border border-dashed border-[#E5E7EB] bg-[#F5F5F5] p-12 text-center">
            <ClockCounterClockwise size={28} weight="duotone" className="mx-auto text-[#002FA7]" />
            <p className="font-display text-xl font-bold mt-4">No generations yet.</p>
            <p className="text-sm text-[#525252] mt-2">Head to the Studio to create your first.</p>
            <Link to="/dashboard" className="btn-primary mt-6" data-testid="history-empty-cta">
              Open Studio <ArrowUpRight size={16} weight="bold" />
            </Link>
          </div>
        )}

        {!loading && items.length > 0 && (
          <>
            <div className="mb-4 border border-[#E5E7EB] bg-white p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">
                  {items.length} history item{items.length === 1 ? "" : "s"} loaded
                </p>
                <p className="text-xs text-[#737373] mt-1">
                  Select items to delete them, or clear the full history.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="border border-[#E5E7EB] px-3 py-2 text-xs font-bold hover:bg-[#F5F5F5] inline-flex items-center gap-1.5"
                >
                  {allSelected ? <CheckSquare size={14} weight="bold" /> : <Square size={14} weight="bold" />}
                  {allSelected ? "Unselect all" : "Select all"}
                </button>

                <button
                  type="button"
                  onClick={askDeleteSelected}
                  disabled={selected.length === 0 || working === "delete-selected"}
                  className="border border-[#DC2626] text-[#DC2626] px-3 py-2 text-xs font-bold hover:bg-[#FEF2F2] disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  <Trash size={14} weight="bold" />
                  {working === "delete-selected" ? "Deleting..." : `Delete selected (${selected.length})`}
                </button>

                <button
                  type="button"
                  onClick={askClearHistory}
                  disabled={working === "clear-history"}
                  className="bg-[#DC2626] text-white px-3 py-2 text-xs font-bold hover:bg-[#B91C1C] disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  <Trash size={14} weight="bold" />
                  {working === "clear-history" ? "Clearing..." : "Clear history"}
                </button>
              </div>
            </div>

            <div className="divide-y divide-[#E5E7EB] border border-[#E5E7EB] bg-white">
              {items.map((it) => {
                const tool = getToolByKey(it.tool);
                const Icon = tool?.icon;
                const isOpen = open === it.id;
                const checked = selectedSet.has(it.id);
                const displayTitle = historyDisplayTitle(it);
                const projectInfo = historyProjectInfo(it);
                const badges = historyMetaBadges(it);

                return (
                  <div key={it.id} data-testid={`history-item-${it.id}`}>
                    <div className="w-full flex items-center justify-between gap-4 p-5 hover:bg-[#F5F5F5] text-left transition-colors">
                      <div className="flex items-center gap-4 min-w-0">
                        <button
                          type="button"
                          onClick={() => toggleSelect(it.id)}
                          className="shrink-0 text-[#002FA7] hover:bg-[#EFF6FF] p-1"
                          aria-label={checked ? "Unselect history item" : "Select history item"}
                        >
                          {checked ? <CheckSquare size={18} weight="bold" /> : <Square size={18} weight="bold" />}
                        </button>

                        {Icon && (
                          <div className="h-10 w-10 bg-[#0A0A0A] text-white flex items-center justify-center shrink-0">
                            <Icon size={18} weight="duotone" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-display font-bold truncate">{displayTitle}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            {badges.map((badge) => (
                              <span
                                key={badge}
                                className="inline-flex border border-[#E5E7EB] bg-white px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.14em] text-[#737373]"
                              >
                                {badge}
                              </span>
                            ))}
                          </div>
                          <p className="text-xs text-[#737373] font-mono mt-1">
                            {new Date(it.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={() => setOpen(isOpen ? null : it.id)}
                          className="border border-[#E5E7EB] px-3 py-2 text-xs font-bold hover:bg-white"
                        >
                          {isOpen ? "Hide" : "View"}
                        </button>

                        <button
                          type="button"
                          onClick={() => askDeleteHistoryItem(it.id)}
                          disabled={working === `delete:${it.id}`}
                          className="border border-[#DC2626] text-[#DC2626] px-3 py-2 text-xs font-bold hover:bg-[#FEF2F2] disabled:opacity-50 inline-flex items-center gap-1.5"
                        >
                          <Trash size={14} weight="bold" />
                          {working === `delete:${it.id}` ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    </div>

                    {isOpen && (
                      <div className="p-6 bg-[#F5F5F5] border-t border-[#E5E7EB]">
                        <div className="border border-[#E5E7EB] bg-white p-4 mb-5">
                          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">
                            Generation details
                          </p>
                          <h3 className="font-display text-xl font-black mt-2">{displayTitle}</h3>
                          {projectInfo?.name && !isGenericProjectLabel(projectInfo.name) && (
                            <p className="mt-2 text-xs font-mono uppercase tracking-[0.16em] text-[#525252]">
                              Project: {projectInfo.name}{["archived", "deleted"].includes(projectInfo.status) ? " (archived)" : ""}
                            </p>
                          )}
                          <div className="mt-3 flex flex-wrap gap-2">
                            {badges.map((badge) => (
                              <span
                                key={badge}
                                className="inline-flex border border-[#E5E7EB] bg-[#F5F5F5] px-2 py-1 text-[10px] font-mono uppercase tracking-[0.14em] text-[#737373]"
                              >
                                {badge}
                              </span>
                            ))}
                          </div>
                        </div>

                        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373] mb-2">
                          Inputs
                        </p>
                        <pre className="text-xs bg-white border border-[#E5E7EB] p-3 overflow-x-auto">
{JSON.stringify(safeHistoryInputs(it.inputs), null, 2)}
                        </pre>
                        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373] mt-5 mb-2">
                          Output
                        </p>
                        <article
                          className="prose-tight bg-white p-5 border border-[#E5E7EB] text-[15px]"
                          dangerouslySetInnerHTML={{ __html: renderMarkdown(it.output) }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </section>

      <HistoryConfirmModal
        open={confirmAction.open}
        title={confirmAction.title}
        subtitle={confirmAction.subtitle}
        message={confirmAction.message}
        confirmLabel={confirmAction.confirmLabel}
        danger
        loading={Boolean(working && ["delete-selected", "clear-history", `delete:${confirmAction.id}`].includes(working))}
        onClose={closeConfirmAction}
        onConfirm={confirmHistoryAction}
      />
    </div>
  );
}
